import React from 'react';
import { useNavigate } from "react-router-dom";
import { Trash } from "react-bootstrap-icons";

import { useForm } from "../../hooks/useForm";
import { formatDate } from "../../utilities/common";
import useSwipeToReveal from "../../hooks/useSwipeToReveal";
import Money from "../../components/Money";
import { useHouseState, useHouseDispatch } from '../../contexts/HouseContext';

import './House.css';

/**
 * House dashboard — Phase 1 visual refresh. Information architecture
 * is unchanged from the previous version: a header with the house
 * description and a quick create-game form, then a list of past
 * games with per-row totals. The mockup's full dashboard layout
 * (Outstanding balances, trend chart, etc.) lands in Phase 3.
 */
const House = () => {
    const houseState = useHouseState();
    const { house, games } = houseState;

    const { addGame } = useHouseDispatch();
    const { handleSubmit, status, message } = useForm(addGame, () => { });

    if (!house) {
        return <div style={{ padding: 48, color: 'var(--text-dim)' }}>Loading…</div>;
    }

    const sessionsCount = Object.keys(games || {}).length;

    return (
        <main className="house">
            <header className="house-mast">
                <div>
                    <div className="house-mast-kicker">House</div>
                    <h1>{renderHouseTitle(house.description)}</h1>
                </div>
                <div className="house-mast-meta">
                    {sessionsCount} {sessionsCount === 1 ? 'session' : 'sessions'}
                </div>
            </header>

            <CreateGamePanel
                handleSubmit={handleSubmit}
                status={status}
                message={message}
            />

            <GameList games={games} />
        </main>
    );
};

/**
 * Titles like "Friday Night Poker" get the last word italicized as
 * the brand's signature accent. If the description is a single word
 * it stays as-is.
 */
function renderHouseTitle(description = "") {
    const trimmed = description.trim();
    if (!trimmed) return null;
    const words = trimmed.split(/\s+/);
    if (words.length === 1) return trimmed;
    const lead = words.slice(0, -1).join(' ');
    const tail = words[words.length - 1];
    return (
        <>
            {lead}{' '}
            <em>{tail}</em>
        </>
    );
}

const CreateGamePanel = ({ handleSubmit, status, message }) => (
    <section className="house-create">
        <span className="house-create-label">New session</span>
        <form onSubmit={handleSubmit}>
            <input
                className="form-control"
                required
                name="description"
                placeholder="What kind of game? e.g. Friday Night #48"
            />
            <input
                className="form-control"
                required
                name="date"
                type="date"
            />
            <button type="submit" className="btn">+ Create</button>
            {status && status !== 'success' && (
                <div className="house-create-status">
                    {status}: {message}
                </div>
            )}
        </form>
    </section>
);

const GameList = ({ games }) => {
    const navigate = useNavigate();
    const entries = Object.entries(games || {});

    if (!entries.length) {
        return (
            <div className="house-list-empty">No sessions yet.</div>
        );
    }

    return (
        <>
            <div className="house-list-head">
                <span></span>
                <span>Session</span>
                <span className="col-money">Buy-ins</span>
                <span className="col-money">Cash-outs</span>
                <span className="col-money">Net</span>
                <span className="col-players">Players</span>
            </div>
            {entries.map(([gameId, g]) => (
                <GameRow key={gameId} game={g} navigate={navigate} />
            ))}
        </>
    );
};

const GameRow = ({ game, navigate }) => {
    const { deleteGame } = useHouseDispatch();
    const { offset, dragging, revealed, handlers } = useSwipeToReveal({
        onTap: () => navigate(`/games/${game.id}`),
    });
    const handleDelete = async () => deleteGame(game.id);

    const dateParts = parseDateParts(game.date);
    const netClass =
        Number(game.totalNet) > 0 ? 'is-up'
        : Number(game.totalNet) < 0 ? 'is-down'
        : 'is-zero';
    const numPlayers = game.numPlayers || 0;

    return (
        <div className="house-row">
            <button
                type="button"
                className="house-row-delete"
                onClick={handleDelete}
                aria-label={`Delete ${game.description}`}
                tabIndex={revealed ? 0 : -1}
            >
                <Trash aria-hidden="true" />
            </button>
            <div
                className={`house-row-grid${dragging ? ' is-dragging' : ''}`}
                style={{ transform: `translateX(${offset}px)` }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/games/${game.id}`);
                    }
                }}
                {...handlers}
            >
                <div className="house-row-date">
                    <strong>{dateParts.day}</strong>
                    <span>{dateParts.month}</span>
                </div>
                <div className="house-row-info">
                    <div className="house-row-title">{game.description}</div>
                    <div
                        className="house-row-meta"
                        data-players={numPlayers}
                    >
                        {formatDate(game.date)}
                    </div>
                </div>
                <div className="house-row-amt house-row-buyins">
                    <Money amount={game.totalBuyIns} />
                </div>
                <div className="house-row-amt house-row-cashouts">
                    <Money amount={game.totalCashOuts} />
                </div>
                <div className={`house-row-amt ${netClass}`}>
                    <Money amount={game.totalNet} signed />
                </div>
                <div className="house-row-players">{numPlayers}</div>
            </div>
        </div>
    );
};

function parseDateParts(date) {
    const d = new Date(date);
    if (isNaN(d.getTime())) return { day: '', month: '' };
    return {
        day: d.getUTCDate().toString().padStart(2, '0'),
        month: d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
    };
}

export default House;
